import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { WpDropdown, WpDropdownToggle, WpDropdownMenu, WpDropdownMenuItem } from 'Wpds/components/dropdown';

import { useFeature } from 'Shared/utils/hooks';
import * as goalsActions from 'Shared/actions/Goals.actions';
import * as GoalsSelectors from 'Shared/selectors/Goals.selectors';
import CreateGoalButtonContainer from 'Shared/components/Goals/CreateGoalButtonContainer/CreateGoalButtonContainer';
import { Feature } from 'Shared/types/Organization.type';
import GoalsEmptyState from 'Shared/components/Goals/EmptyState/GoalsEmptyState';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import Spinner from 'Shared/components/Spinner';
import FreePlanGoalsContent from 'Shared/components/FreePlanGoalsContent/FreePlanGoalsContent';

import OneOnOneAndTeamSyncUserGoals from './OneOnOneAndTeamSyncUserGoals';

import './WorkspaceGoals.scss';

// TODO: (noah / josh) - we should return the workspace slug in the goal attributes instead of the db id
type Props = {
  workspaceId: string;
  isPersonal: boolean;
};

function OneOnOneAndTeamSyncGoals({ workspaceId, isPersonal }: Props) {
  const dispatch = useDispatch();
  const { isFreePlan } = useFeature(Feature.goals);

  const [isLoading, setIsLoading] = useState(true);
  const [isViewingComplete, setIsViewingComplete] = useState(false);
  const completeFilterLabel = isViewingComplete ? 'Completed Goals' : 'Open Goals';

  const params = {
    context: 'workspace',
    workspace_slug: workspaceId,
  };

  const users = useSelector(state => state.workspace.users) || [];

  useEffect(() => {
    async function getGoals() {
      setIsLoading(true);
      await dispatch(goalsActions.fetchGoals(params));
      setIsLoading(false);
    }
    getGoals();
  }, []);

  const hasCompleteGoalsInWorkspace = !useSelector(state =>
    GoalsSelectors.getGoalsByWorkspaceAndComplete(state, { complete: true, workspaceSlug: workspaceId })
  ).isEmpty();

  const hasCompletePublicGoals = !useSelector(state =>
    GoalsSelectors.getGoalsByListOfUsersAndPrivacy(state, {
      users,
      complete: true,
      privacySetting: 'everyone',
    })
  ).isEmpty();

  const hasCompleteGoals = hasCompleteGoalsInWorkspace || hasCompletePublicGoals;

  const goalsInView = !useSelector(state =>
    GoalsSelectors.getGoalsByWorkspaceAndComplete(state, { complete: isViewingComplete, workspaceSlug: workspaceId })
  ).isEmpty();

  const hasPublicGoalsInView = !useSelector(state =>
    GoalsSelectors.getGoalsByListOfUsersAndPrivacy(state, {
      users,
      complete: isViewingComplete,
      privacySetting: 'everyone',
    })
  ).isEmpty();

  const hasGoalsInView = goalsInView || hasPublicGoalsInView;

  const showEmptyState = !hasGoalsInView;
  const showTitle = !showEmptyState;

  let emptyStateVariant;
  if (!isViewingComplete && hasCompleteGoals) {
    emptyStateVariant = 'workspaceOpenSomeComplete';
  } else if (!isViewingComplete && !hasCompleteGoals) {
    emptyStateVariant = 'workspaceOpenNoneComplete';
  } else {
    emptyStateVariant = 'workspaceComplete';
  }

  return (
    <div className="WorkspaceGoals">
      {isLoading && !isFreePlan && <Spinner activeIf />}
      {!isLoading && !isFreePlan && (
        <>
          <div className="d-flex justify-content-between">
            <div className="d-flex align-items-center">
              <span className="mr-2">Show: </span>
              <WpDropdown>
                <WpDropdownToggle>
                  <WpButton color="primary" variant="tertiary" size="sm" square slimPadding>
                    {completeFilterLabel}
                    <WpIcon name="chevron-down" />
                  </WpButton>
                </WpDropdownToggle>
                <WpDropdownMenu>
                  <WpDropdownMenuItem onClick={() => setIsViewingComplete(false)}>Open Goals</WpDropdownMenuItem>
                  <WpDropdownMenuItem onClick={() => setIsViewingComplete(true)}>Completed Goals</WpDropdownMenuItem>
                </WpDropdownMenu>
              </WpDropdown>
            </div>
            {!showEmptyState && (
              <CreateGoalButtonContainer>
                <WpButton>Create Goal</WpButton>
              </CreateGoalButtonContainer>
            )}
          </div>
          {showTitle && <div className="WorkspaceGoals__goals pt-4">Goals</div>}

          {users.map(user => (
            <OneOnOneAndTeamSyncUserGoals
              key={`workspaceUserGoal-${user.id}`}
              user={user}
              isViewingComplete={isViewingComplete}
              workspaceSlug={workspaceId}
              isPersonal={isPersonal}
            />
          ))}
          {showEmptyState && <GoalsEmptyState variant={emptyStateVariant} />}
        </>
      )}
      {isFreePlan && <FreePlanGoalsContent />}
    </div>
  );
}

export default OneOnOneAndTeamSyncGoals;
